import { BlackPiece, BoardState, Kings, Piece, PlayerColor, WhitePiece } from 'features/board/board'
import {
  getNonKingPiece,
  isBlackKingPiece,
  isBlackPiece,
  isEmptyHex,
  isPiece,
  isWhiteKingPiece,
  isWhitePiece,
  killsMe,
  resolveConflicts,
  someNeighborsKillsHex,
} from 'features/board/utils'
import { HexBoard, HexId } from 'features/hex-board/hex-board'
import { getNeighborPieces, getNeighbors } from 'features/hex-board/utils'

import { getEntries } from 'shared/utils'

export type Weights = typeof defaultWeights

type BoardData = {
  white: {
    kk: number
    nkk: number
    mobility: number
    touchingPieces: number
    kingDistanceScore: number
    hand: { kk: number; nkk: number }
    death: { kk: number; nkk: number }
    elements: { l: number; f: number; w: number }
  }
  black: {
    kk: number
    nkk: number
    mobility: number
    touchingPieces: number
    kingDistanceScore: number
    hand: { kk: number; nkk: number }
    death: { kk: number; nkk: number }
    elements: { L: number; F: number; W: number }
  }
}

export const defaultWeights = {
  pieceWeight: 1,
  killerWeight: 2,
  pieceKingProximityWeight: 1,
  killerKingProximityWeight: 100,
  pieceMobilityWeight: 0.1,
  kingShieldWeight: 1.5,
  kingWeight: 10000,

  potentialDeathPenalty: -2,
  potentialKillerDeathPenalty: -3,
  kingsEmptySpacePenalty: -1,
  touchingPiecesPenalty: -0.5,
  fightPenalty: -0.2,
  kingMovedPenalty: -2,
  pieceDiversityPenalty: -3,
}

const getDistance = (hexId1: HexId, hexId2: HexId): number => {
  const [x1, y1] = hexId1.split('_').map(Number)
  const [x2, y2] = hexId2.split('_').map(Number)
  return Math.max(Math.abs(x1 - x2), Math.abs(y1 - y2), Math.abs(x1 + y1 - x2 - y2))
}

const calculateDistanceImportance = (distance: number, pieceCount: number) => {
  if (pieceCount > 8) {
    if (distance === 1) return 1
    if (distance === 2) return 0.1

    return 0
  } else {
    return Math.exp(-distance) / 2
  }
}

const isKingKiller = (piece: Piece, kings: Kings) => {
  if ((isWhitePiece(piece) || isWhiteKingPiece(piece)) && killsMe(kings.black.piece, piece)) {
    return true
  } else if ((isBlackPiece(piece) || isBlackKingPiece(piece)) && killsMe(kings.white.piece, piece)) {
    return true
  }

  return false
}

const evaluatePieces = ({ black, white }: BoardData, w = defaultWeights) => {
  let score = 0

  score += white.kk * w.killerWeight + white.nkk * w.pieceWeight
  score -= black.kk * w.killerWeight + black.nkk * w.pieceWeight

  return score
}

const evaluateElementsDiversity = ({ white, black }: BoardData, w = defaultWeights) => {
  let score = 0

  Object.values(white.elements).forEach(pieceCount => {
    if (pieceCount === 0) {
      score += w.pieceDiversityPenalty
    }
  })

  Object.values(black.elements).forEach(pieceCount => {
    if (pieceCount === 0) {
      score -= w.pieceDiversityPenalty
    }
  })

  return score
}

const evaluatePotentialDeathCount = ({ white, black }: BoardData, w = defaultWeights) => {
  let score = 0

  score += white.death.kk * w.potentialKillerDeathPenalty + white.death.nkk * w.potentialDeathPenalty
  score -= black.death.kk * w.potentialKillerDeathPenalty + black.death.nkk * w.potentialDeathPenalty

  return score
}

const evaluateKingsDefense = (hexBoard: HexBoard, pieceCount: number, kings: Kings, w = defaultWeights) => {
  let score = 0

  if (pieceCount < 12) return 0

  if (kings.white.hexId) {
    const neighborPieces = getNeighborPieces(kings.white.hexId, hexBoard)
    const emptyPieces = neighborPieces.filter(isEmptyHex).length
    const friendlyPieces = neighborPieces.filter(isWhitePiece).length

    score += emptyPieces * w.kingsEmptySpacePenalty
    score += friendlyPieces * w.kingShieldWeight
  }

  if (kings.black.hexId) {
    const neighborPieces = getNeighborPieces(kings.black.hexId, hexBoard)
    const emptyPieces = neighborPieces.filter(isEmptyHex).length
    const friendlyPieces = neighborPieces.filter(isBlackPiece).length

    score -= emptyPieces * w.kingsEmptySpacePenalty
    score -= friendlyPieces * w.kingShieldWeight
  }

  return score
}

const evaluateTouchingPieces = ({ white, black }: BoardData, w = defaultWeights) => {
  let score = 0

  score += white.touchingPieces * w.touchingPiecesPenalty
  score -= black.touchingPieces * w.touchingPiecesPenalty

  return score
}

const evaluateFightClicks = (fightClicks: BoardState['fightClicks'], w = defaultWeights) => {
  let score = 0

  score += fightClicks.white * w.fightPenalty
  score -= fightClicks.black * w.fightPenalty

  return score
}

const evaluateKingDistance = ({ black, white }: BoardData) => {
  let score = 0

  score += white.kingDistanceScore
  score += black.kingDistanceScore
  return score
}

const evaluateKingMoved = (kingMoved: BoardState['kingMoved'], w = defaultWeights) => {
  let score = 0

  if (kingMoved.white) score += w.kingMovedPenalty
  if (kingMoved.black) score -= w.kingMovedPenalty

  return score
}

const evaluatePieceMobility = ({ black, white }: BoardData, w = defaultWeights) => {
  let score = 0

  score += white.mobility * w.pieceMobilityWeight
  score -= black.mobility * w.pieceMobilityWeight

  return score
}

const evaluateFightTurn = (hexBoard: HexBoard, turn: PlayerColor, kings: Kings) => {
  const newBoardState = resolveConflicts(hexBoard)

  if (
    kings.white.hexId &&
    !newBoardState[kings.white.hexId] &&
    kings.black.hexId &&
    !newBoardState[kings.black.hexId]
  ) {
    return 0
  }

  if (kings.white.hexId && !newBoardState[kings.white.hexId] && turn === 'black') {
    return -defaultWeights.kingWeight
  }

  if (kings.black.hexId && !newBoardState[kings.black.hexId] && turn === 'white') {
    return defaultWeights.kingWeight
  }

  return 0
}

const evaluateKingKillers = ({ black, white }: BoardData, kings: Kings) => {
  if (kings.black.hexId && kings.white.hexId) {
    if (!black.kk && !white.kk) return 0

    if (!black.kk) return defaultWeights.kingWeight / 2
    if (!white.kk) return -defaultWeights.kingWeight / 2
  }
  return 0
}

const collectBoardData = (
  hexBoard: HexBoard,
  hand: BoardState['hand'],
  kings: Kings,
  pieceCount: number,
  w = defaultWeights
): BoardData => {
  const white: BoardData['white'] = {
    kk: 0,
    nkk: 0,
    mobility: 0,
    touchingPieces: 0,
    kingDistanceScore: 0,
    hand: { kk: 0, nkk: 0 },
    death: { kk: 0, nkk: 0 },
    elements: { l: 0, f: 0, w: 0 },
  }

  const black: BoardData['black'] = {
    kk: 0,
    nkk: 0,
    mobility: 0,
    touchingPieces: 0,
    kingDistanceScore: 0,
    hand: { kk: 0, nkk: 0 },
    death: { kk: 0, nkk: 0 },
    elements: { L: 0, F: 0, W: 0 },
  }

  // Sets to track hexes that have been counted
  const whitePossibleDeploy = new Set<HexId>()
  const blackPossibleDeploy = new Set<HexId>()

  for (const piece of hand.white) {
    if (isKingKiller(piece, kings)) {
      white.kk++
      white.hand.kk++
    } else {
      white.nkk++
      white.hand.nkk++
    }

    white.elements[piece as WhitePiece]++
  }

  for (const piece of hand.black) {
    if (isKingKiller(piece, kings)) {
      black.kk++
      black.hand.kk++
    } else {
      black.nkk++
      black.hand.nkk++
    }

    black.elements[piece as BlackPiece]++
  }

  getEntries(hexBoard).forEach(([hexId, piece]) => {
    // Can be used for deployment for king proximity
    if (isEmptyHex(piece)) {
      const nbPieces = getNeighborPieces(hexId, hexBoard)
      const whiteCanDeploy = nbPieces.filter(nb => isWhitePiece(nb) || isWhiteKingPiece(nb)).length > 0
      const blackCanDeploy = nbPieces.filter(nb => isBlackPiece(nb) || isBlackKingPiece(nb)).length > 0

      if (whiteCanDeploy && kings.black.hexId && !whitePossibleDeploy.has(hexId)) {
        const importance = calculateDistanceImportance(getDistance(kings.black.hexId, hexId) + 1, pieceCount)

        whitePossibleDeploy.add(hexId)
        white.kingDistanceScore +=
          (importance * Math.min(1, white.hand.kk) * w.killerKingProximityWeight +
            importance * Math.min(1, white.hand.nkk) * w.pieceKingProximityWeight) /
          5
      }

      if (blackCanDeploy && kings.white.hexId && !blackPossibleDeploy.has(hexId)) {
        const importance = calculateDistanceImportance(getDistance(kings.white.hexId, hexId) + 1, pieceCount)

        blackPossibleDeploy.add(hexId)
        black.kingDistanceScore +=
          (importance * Math.min(1, black.hand.kk) * w.killerKingProximityWeight +
            importance * Math.min(1, black.hand.nkk) * w.pieceKingProximityWeight) /
          5
      }
    }

    if (!isPiece(piece)) return

    const isKK = isKingKiller(piece, kings)
    const isWhite = isWhitePiece(piece) || isWhiteKingPiece(piece)
    const isBlack = isBlackPiece(piece) || isBlackKingPiece(piece)
    const nb = getNeighbors(hexId)
    const nbPieces = getNeighborPieces(hexId, hexBoard)

    if (isWhite) {
      if (isKK) white.kk++
      else white.nkk++

      white.elements[getNonKingPiece(piece) as WhitePiece]++

      white.mobility += nb.filter(h => isEmptyHex(hexBoard[h]) || isWhitePiece(piece) || isWhiteKingPiece(piece)).length
      white.touchingPieces += nbPieces.filter(
        neighbor => isPiece(neighbor) && getNonKingPiece(neighbor) === getNonKingPiece(piece)
      ).length
      if (someNeighborsKillsHex(hexId, hexBoard)) {
        if (isKK) {
          white.death.kk++
        } else {
          white.death.nkk++
        }
      }

      if (kings.black.hexId) {
        const importance = calculateDistanceImportance(getDistance(kings.black.hexId, hexId), pieceCount)
        white.kingDistanceScore += importance * (isKK ? w.killerKingProximityWeight : w.pieceKingProximityWeight)
      }
    } else if (isBlack) {
      if (isKK) black.kk++
      else black.nkk++

      black.elements[getNonKingPiece(piece) as BlackPiece]++

      black.mobility += nb.filter(h => isEmptyHex(hexBoard[h]) || isBlackPiece(piece) || isBlackKingPiece(piece)).length
      black.touchingPieces += nbPieces.filter(
        neighbor => isPiece(neighbor) && getNonKingPiece(neighbor) === getNonKingPiece(piece)
      ).length
      if (someNeighborsKillsHex(hexId, hexBoard)) {
        if (isKK) {
          black.death.kk++
        } else {
          black.death.nkk++
        }
      }

      if (kings.white.hexId) {
        const importance = calculateDistanceImportance(getDistance(kings.white.hexId, hexId), pieceCount)
        black.kingDistanceScore -= importance * (isKK ? w.killerKingProximityWeight : w.pieceKingProximityWeight)
      }
    }
  })

  return { white, black }
}

export const evaluatePosition = (boardState: BoardState, w = defaultWeights): number => {
  let score = 0

  const { hand, hexBoard, kings, turn, phase, fightClicks, kingMoved, pieceCount } = boardState

  const data = collectBoardData(hexBoard, hand, kings, pieceCount, w)

  let evalKingKillers = 0
  let evalFightTurn = 0
  if (phase === 'fight') {
    if (!kings.white.piece && !kings.black.piece) return 0

    if (!kings.white.piece) score -= defaultWeights.kingWeight
    if (!kings.black.piece) score += defaultWeights.kingWeight

    evalKingKillers = evaluateKingKillers(data, kings)
    evalFightTurn = evaluateFightTurn(hexBoard, turn, kings)
  }

  const evalPieces = evaluatePieces(data, w)
  const evalElementsDiversity = evaluateElementsDiversity(data, w)
  const evalPotentialDeathCount = evaluatePotentialDeathCount(data, w)
  const evalTouchingPieces = evaluateTouchingPieces(data, w)
  const evalKingDistance = evaluateKingDistance(data)
  const evalPieceMobility = evaluatePieceMobility(data, w)
  const evalKingsDefense = evaluateKingsDefense(hexBoard, pieceCount, kings, w)
  const evalKingMoved = evaluateKingMoved(kingMoved, w)
  const evalFightClicks = evaluateFightClicks(fightClicks, w)

  score += evalPieces
  score += evalKingDistance
  score += evalKingKillers
  score += evalElementsDiversity
  score += evalFightTurn
  score += evalPotentialDeathCount
  score += evalTouchingPieces
  score += evalPieceMobility
  score += evalKingsDefense
  score += evalKingMoved
  score += evalFightClicks

  // const a = true
  const a = false

  if (a) {
    console.log('evalPieces', evalPieces)
    console.log('evalElementsDiversity', evalElementsDiversity)
    console.log('evalPotentialDeathCount', evalPotentialDeathCount)
    console.log('evalTouchingPieces', evalTouchingPieces)
    console.log('evalKingDistance', evalKingDistance)
    console.log('evalPieceMobility', evalPieceMobility)
    console.log('evalKingKillers', evalKingKillers)
    console.log('evalKingsDefense', evalKingsDefense)
    console.log('evalKingMoved', evalKingMoved)
    console.log('evalFightClicks', evalFightClicks)
    console.log('evalFightTurn', evalFightTurn)
  }

  return Math.round((score + Number.EPSILON) * 100) / 100
}
